<template>
  <div v-if="isStatsValid" class="upload-stats">
    <PayAsYouGoModal ref="payAsYouGoModal"/>
    <div class="upload-stats__head">
      <div class="d-flex justify-content-between">
        <div>
          <div class="d-flex align-items-center">
            <CIcon name="cipCube01" size="custom" :height="16" class="mr-2"/>
            <h6 v-if="stats.plan_name">{{ stats.plan_name }}</h6>
            <h6 v-else v-translate translate-context="upload.stats.title">Media</h6>
          </div>
          <div class="subtitle">
            {{ stats.process_limit }}
            <translate translate-context="upload.stats">photo uploads included</translate>
          </div>
        </div>
        <CLink @click="loadStats(600)">
          <CIcon name="cipReload" class=" ml-1"
                 style="vertical-align: sub" size="custom" :height="16"/>
        </CLink>
      </div>
    </div>
    <div class="d-flex align-items-center justify-content-center">
      <CSpinner v-if="loading" size="sm" color="primary" class="mx-auto position-absolute"/>
      <div class="d-flex align-items-center w-100" :class="{'invisible': loading}">

        <ProgressRing :percentages="ringsPercentages" style="width: 110px">
          <template #label>
            <span class="font-weight-semi-bold">{{
                delivered + in_processing
              }}</span>/<br>{{ limit }}
          </template>
        </ProgressRing>
        <div class="upload-stats__legend flex-grow-1">
          <div class="d-flex align-items-center">
            <CIcon name="cipDot" size="custom" :height="8" class="text-primary mr-2"/>
            <div>
              <div class="font-weight-semi-bold">
                {{ delivered }}
                <translate translate-context="upload.stats">photos</translate>
              </div>
              <div>
                <translate translate-context="upload.stats">delivered</translate>
              </div>
            </div>
          </div>
          <hr style="margin: 8px 0"/>
          <div class="d-flex align-items-center">
            <CIcon name="cipDot" size="custom" :height="8" class="text-danger mr-2"/>
            <div>
              <div class="font-weight-semi-bold">
                {{ in_processing }}
                <translate translate-context="upload.stats">photos</translate>
              </div>
              <div>
                <translate translate-context="upload.stats">processing</translate>
              </div>
            </div>
          </div>
          <hr style="margin: 8px 0" v-if="deliverable > 0"/>
          <div class="d-flex align-items-center" v-if="deliverable > 0">
            <CIcon name="cipDot" size="custom" :height="8" class="text-warning mr-2"/>
            <div>
              <div class="font-weight-semi-bold">
                {{ deliverable }}
                <translate translate-context="upload.stats">photos</translate>
              </div>
              <div>
                <translate translate-context="upload.stats">deliverable</translate>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="upload-stats__footer" v-if="requirePayment">
      <CLink @click="onPaymentRequired">
        <translate>Deliver now</translate>
        <CIcon name="cipArrowRight" class="text-primary ml-1"
               style="vertical-align: sub" size="custom" :height="16"/>
      </CLink>
    </div>
  </div>

</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ProgressRing from "@/domain/events/components/ProgressRings.vue";
import PayAsYouGoModal from "@/domain/events/components/PayAsYouGoModal.vue";
import MessageManager from "@/domain/core/utils/messageManager";

export default {
  name: "UploadStats",
  components: {PayAsYouGoModal, ProgressRing},
  data() {
    return {
      loading: false
    }
  },
  async mounted() {
    await this.setPicaServiceId(this.$route.params.picaServiceId)
    MessageManager.onProcessed('uploadStats',(processed) => this.updateStats({processed: processed}))
    MessageManager.onUploaded('uploadStats',(uploaded) => this.updateStats({uploaded: uploaded}))
    await this.loadStats()
  },
  beforeDestroy() {
    MessageManager.removeCallbacks('uploadStats')
  },
  computed: {
    ...mapGetters('uploader', [
      'stats',
    ]),
    isStatsValid() {
      if (!this.stats) return false
      if (this.limit <= 0) return false
      return true
    },
    ringsPercentages() {
      return [
        this.delivered / this.limit * 100,
        this.in_processing / this.limit * 100,
        this.deliverable / this.limit * 100
      ]
    },
    limit() {
      let totalMedia = this.stats.uploaded + this.stats.processed
      if (totalMedia > this.stats.process_limit) {
        return this.stats.upload_limit
      } else {
        return this.stats.process_limit
      }
    },
    requirePayment() {
      return this.deliverable > 0
    },
    delivered() {
      if (this.stats.processed >= this.stats.process_limit) {
        return this.stats.process_limit
      } else {
        return this.stats.processed
      }
    },
    in_processing() {
      const inProcessing = this.stats.uploaded - this.stats.processed
      if (inProcessing > this.stats.process_limit) {
        return 0
      } else {
        return inProcessing
      }
    },
    deliverable() {
      if (this.stats.processed >= this.stats.process_limit) {
        return this.stats.uploaded - this.stats.process_limit
      } else {
        return 0
      }
    }
  },
  methods: {
    ...mapActions('uploader', [
      'fetchStats',
      'setPicaServiceId',
      'updateStats'
    ]),
    async loadStats(delay = 0) {
      this.loading = true
      await this.delay(delay)
      await this.fetchStats()
      this.loading = false
    },
    onPaymentRequired() {
      this.$refs.payAsYouGoModal.open()
    }
  }
}
</script>